import { injectable, interfaces } from 'inversify';
import { action, makeObservable, observable, ObservableMap } from 'mobx';

export interface IOverlayLoaderStore {
  isOverlayVisible(): boolean;
  isSpinnerVisible(key?: string): boolean;
  enableLoader(buttonId: string): void;
  disableLoader(buttonId: string): void;
  withLoaderAsync<T>(buttonId: string, action: () => Promise<T>): Promise<T>;
  hideOverlay(): void;
}

export namespace IOverlayLoaderStore {
  export const $: interfaces.ServiceIdentifier<IOverlayLoaderStore> = Symbol('OVERLAY_LOADER_STORE');
}

@injectable()
export class OverlayLoaderStore implements IOverlayLoaderStore {
  spinners: ObservableMap<string, boolean> = observable.map();

  constructor() {
    makeObservable(this, {
      spinners: observable,
      hideOverlay: action,
      enableLoader: action,
      disableLoader: action,
      withLoaderAsync: action,
    });
  }

  isOverlayVisible = (): boolean => {
    return this.spinners.size > 0 && Array.from(this.spinners.values()).some((value) => value);
  };

  isSpinnerVisible = (key?: string): boolean => {
    if (key === undefined || !this.spinners.has(key)) {
      return false;
    }

    return this.spinners.get(key) ?? false;
  };

  hideOverlay = (): void => {
    for (const key in this.spinners) {
      this.disableLoader(key);
    }
  };

  enableLoader = (key: string): void => {
    this.spinners.set(key, true);
  };

  disableLoader = (key: string): void => {
    this.spinners.set(key, false);
  };

  async withLoaderAsync<T>(key: string, action: () => Promise<T>): Promise<T> {
    this.enableLoader(key);
    try {
      return await action();
    } finally {
      this.disableLoader(key);
    }
  }
}
